import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Copy,
  Check,
  Clock,
  User,
  Code,
  FileText,
  Terminal,
  Cpu,
  AlertCircle,
  AlertTriangle,
  Info,
} from "lucide-react";
import { format } from "date-fns";
import { useState } from "react";
import type { UnifiedError } from "@/lib/error-types";
import { getErrorCodeInfo } from "@/lib/error-codes";
import { getSeverityBadgeClass } from "./SeverityToggle";
import { cn } from "@/lib/utils";

interface ErrorDetailDrawerProps {
  error: UnifiedError | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Simple JSON syntax highlighter
function JsonHighlight({ data }: { data: unknown }) {
  const jsonString = JSON.stringify(data, null, 2);

  return (
    <pre className="text-xs font-mono bg-muted p-4 rounded-md overflow-x-auto whitespace-pre-wrap break-words">
      <code>{jsonString}</code>
    </pre>
  );
}

// Copy button with feedback
function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleCopy}
      className="h-7 text-xs"
    >
      {copied ? (
        <>
          <Check className="h-3 w-3 mr-1" />
          Copied
        </>
      ) : (
        <>
          <Copy className="h-3 w-3 mr-1" />
          {label || "Copy"}
        </>
      )}
    </Button>
  );
}

// Get severity icon
function SeverityIcon({ severity }: { severity: UnifiedError["severity"] }) {
  switch (severity) {
    case "error":
      return <AlertCircle className="h-4 w-4 text-destructive" />;
    case "warning":
      return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
    case "info":
    default:
      return <Info className="h-4 w-4 text-muted-foreground" />;
  }
}

// Get source icon
function SourceIcon({ source }: { source: UnifiedError["source"] }) {
  switch (source) {
    case "compiler":
      return <FileText className="h-4 w-4" />;
    case "auth":
      return <User className="h-4 w-4" />;
    case "llm":
      return <Cpu className="h-4 w-4" />;
    case "function":
      return <Terminal className="h-4 w-4" />;
    case "extraction":
      return <Code className="h-4 w-4" />;
    default:
      return <AlertCircle className="h-4 w-4" />;
  }
}

export function ErrorDetailDrawer({
  error,
  open,
  onOpenChange,
}: ErrorDetailDrawerProps) {
  if (!error) return null;

  const codeInfo = getErrorCodeInfo(error.code, error.source);

  // Format context for display
  const contextEntries = Object.entries(error.context).filter(
    ([_, v]) => v !== undefined && v !== null
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-hidden flex flex-col">
        <SheetHeader className="space-y-1">
          <div className="flex items-center gap-2">
            <SeverityIcon severity={error.severity} />
            <SheetTitle className="text-lg">Error Details</SheetTitle>
          </div>
          <SheetDescription className="sr-only">
            Detailed view of the selected error
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="flex-1 -mx-6 px-6">
          <div className="space-y-6 pb-6">
            {/* Error Overview */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <SourceIcon source={error.source} />
                <span className="text-sm font-medium capitalize">
                  {error.source}
                </span>
                <Badge className={cn("ml-auto", getSeverityBadgeClass(error.severity))}>
                  {error.severity}
                </Badge>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    Error Code
                  </span>
                  <Badge variant="outline" className="font-mono text-xs">
                    {error.code}
                  </Badge>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    Timestamp
                  </span>
                  <span className="text-sm flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {format(new Date(error.created_at), "MMM d, yyyy HH:mm:ss")}
                  </span>
                </div>

                {error.user_id && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      User ID
                    </span>
                    <span className="text-sm font-mono">
                      {error.user_email || error.user_id.slice(0, 8) + "..."}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Message */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Message</h4>
              <p className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                {error.message}
              </p>
            </div>

            {/* Code Info */}
            {codeInfo.description && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Description</h4>
                <p className="text-sm text-muted-foreground">
                  {codeInfo.description}
                </p>
                {codeInfo.resolution && (
                  <p className="text-sm text-blue-600 dark:text-blue-400">
                    <strong>Resolution:</strong> {codeInfo.resolution}
                  </p>
                )}
              </div>
            )}

            <Separator />

            {/* Context */}
            {contextEntries.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Context</h4>
                <div className="grid gap-2">
                  {contextEntries.map(([key, value]) => (
                    <div
                      key={key}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-muted-foreground">
                        {key.replace(/_/g, " ")}
                      </span>
                      <span className="font-mono text-xs truncate max-w-[200px]">
                        {String(value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Separator />

            {/* Raw Payload */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">Raw Payload</h4>
                <CopyButton
                  text={JSON.stringify(error.raw_payload, null, 2)}
                  label="Copy JSON"
                />
              </div>
              {error.raw_payload ? (
                <JsonHighlight data={error.raw_payload} />
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  No payload data available
                </p>
              )}
            </div>

            {/* Full Error Copy */}
            <div className="pt-4">
              <CopyButton
                text={JSON.stringify(error, null, 2)}
                label="Copy Full Error"
              />
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
